import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type CirculationSettings = {
  allowCrossBranchCheckout: boolean;
  blockCheckoutFineThreshold: number | null;
  gracePeriodDays: number;
  autoRenewalEnabled: boolean;
  holdShelfDays: number;
  renewalWindowDays: number;
  renewalLimitScope: "per_checkout" | "per_patron";
};

type CirculationSettingsSectionProps = {
  settings: CirculationSettings;
  onChange: (next: CirculationSettings) => void;
  disabled?: boolean;
};

export function CirculationSettingsSection({
  settings,
  onChange,
  disabled,
}: CirculationSettingsSectionProps) {
  return (
    <div className="space-y-5">
      <ToggleRow
        id="allowCrossBranch"
        checked={settings.allowCrossBranchCheckout}
        disabled={disabled}
        label="Allow cross-branch checkout"
        hint="Staff can check out items owned by other branches."
        onChange={(v) => onChange({ ...settings, allowCrossBranchCheckout: v })}
      />
      <ToggleRow
        id="autoRenewal"
        checked={settings.autoRenewalEnabled}
        disabled={disabled}
        label="Enable automatic renewals"
        hint="Eligible checkouts renew before due date unless held."
        onChange={(v) => onChange({ ...settings, autoRenewalEnabled: v })}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField
          id="fineThreshold"
          label="Fine threshold to block checkout (₦)"
          hint="Leave empty for no limit."
          value={settings.blockCheckoutFineThreshold ?? ""}
          disabled={disabled}
          prefix="₦"
          placeholder="No limit"
          onChange={(raw) =>
            onChange({
              ...settings,
              blockCheckoutFineThreshold: raw === "" ? null : parseFloat(raw) || 0,
            })
          }
        />
        <NumberField
          id="gracePeriod"
          label="Overdue grace period (days)"
          hint="Days after due date before fines start."
          value={settings.gracePeriodDays}
          disabled={disabled}
          onChange={(raw) => onChange({ ...settings, gracePeriodDays: parseInt(raw) || 0 })}
        />
        <NumberField
          id="holdShelfDays"
          label="Hold shelf period (days)"
          hint="How long a held item stays on the shelf."
          value={settings.holdShelfDays}
          disabled={disabled}
          onChange={(raw) => onChange({ ...settings, holdShelfDays: parseInt(raw) || 7 })}
        />
        <NumberField
          id="renewalWindow"
          label="Renewal window (days before due)"
          hint="0 allows renewal at any time."
          value={settings.renewalWindowDays}
          disabled={disabled}
          onChange={(raw) => onChange({ ...settings, renewalWindowDays: parseInt(raw) || 0 })}
        />
      </div>

      <div className="space-y-2">
        <Label>Renewal limit scope</Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            [
              ["per_patron", "Per patron", "Limit shared across all active checkouts"],
              ["per_checkout", "Per checkout", "Each item gets its own renewal limit"],
            ] as const
          ).map(([value, title, desc]) => (
            <label
              key={value}
              className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                settings.renewalLimitScope === value
                  ? "border-primary/40 bg-primary/5"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="renewalScope"
                className="mt-1 accent-primary"
                disabled={disabled}
                checked={settings.renewalLimitScope === value}
                onChange={() => onChange({ ...settings, renewalLimitScope: value })}
              />
              <span>
                <span className="block text-sm font-medium text-slate-900">{title}</span>
                <span className="text-xs text-slate-500">{desc}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  id,
  checked,
  disabled,
  label,
  hint,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  hint: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(c) => onChange(c === true)}
        />
        <Label htmlFor={id} className="cursor-pointer font-medium">
          {label}
        </Label>
      </div>
      <p className="mt-1.5 pl-6 text-sm text-slate-500">{hint}</p>
    </div>
  );
}

function NumberField({
  id,
  label,
  hint,
  value,
  disabled,
  prefix,
  placeholder,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: number | string;
  disabled?: boolean;
  prefix?: string;
  placeholder?: string;
  onChange: (raw: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        {prefix ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          type="number"
          min={0}
          step={prefix ? 0.01 : 1}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={prefix ? "pl-8" : undefined}
        />
      </div>
      <p className="text-xs text-slate-500">{hint}</p>
    </div>
  );
}
